import { SunFogIcon } from "@assets/icons"
import HospitalIcon from "@assets/icons/hospital"
import MeetingInfoBackgroundIcon from "@assets/icons/meetingInfoBackground"
import MonitorRecorderIcon from "@assets/icons/monitorRecorder"
import SunIcon from "@assets/icons/sun"
import { Button } from "@components/Button"
import FullpageLoadingState from "@components/Common/FullpageLoadingState"
import AppointmentCalendar from "@containers/ManageAppointmentPage/AppointmentCalendar"
import useBooking from "@hooks/useBooking"
import { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router"
import getErrorStatement from "src/utils/getErrorStatement"
import { notify } from "src/utils/notify"

const MeetingInfo = ({
  setAppointmentStage,
  appointmentDetails,
  setAppointmentDetails,
  filterParams,
}) => {
  const [selectedSlot, setSelectedSlot] = useState("11 : 15")
  const { isGetCalendarBookingLoading } = useSelector((state) => state.booking)
  const { handleGetCalendarBooking } = useBooking()
  const [selectedDate, setSelectedDate] = useState(() => {
    const date = new Date()
    return date.toISOString()
  })
  const navigate = useNavigate()
  const { getCalendarBookingResponse } = useSelector((state) => {
    return state.booking
  })
  const currentFormFields = [
    "startDateTime",
    "endDateTime",
    "duration",
    "type",
    "location",
  ]
  const isDisabled = !currentFormFields.every((item) => {
    return appointmentDetails[item] ? true : false
  })

  const currentBooking = getCalendarBookingResponse?.currentBooking
  const handleCalendarDateChange = (value) => {
    // Validate that the selected date is not in the past
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const selectedDate = new Date(value)
    selectedDate.setHours(0, 0, 0, 0)

    // If the selected date is before today, don't proceed with the selected date
    if (selectedDate < today) {
      notify(
        "Cannot select a date in the past. Today's date has been selected."
      )

      // Instead of returning, reset to today's date
      handleGetCalendarBooking(
        { date: today.toISOString() },
        () => {
          setSelectedDate(today.toISOString())

          // Reset selected slot when date changes
          setSelectedSlot(null)

          // Clear the startDateTime and endDateTime values
          if (appointmentDetails.startDateTime) {
            setAppointmentDetails((prev) => ({
              ...prev,
              startDateTime: null,
              endDateTime: null,
            }))
          }
        },
        (error) => {
          notify(getErrorStatement(error))
        }
      )
      return
    }

    // If date is valid (not in the past), proceed normally
    handleGetCalendarBooking(
      { date: value.toISOString() },
      () => {
        setSelectedDate(value.toISOString())

        // Reset selected slot when date changes
        setSelectedSlot(null)

        // Clear the startDateTime and endDateTime values when changing dates
        if (appointmentDetails.startDateTime) {
          setAppointmentDetails((prev) => ({
            ...prev,
            startDateTime: null,
            endDateTime: null,
          }))
        }
      },
      (error) => {
        notify(getErrorStatement(error))
      }
    )
  }

  // Format the date for display
  const formatDateForDisplay = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  const appointmentTimingsJsx = (
    startHour,
    startMinutes,
    endHour,
    endMinutes
  ) => {
    let outputJsx = []
    const isbuttonSlotDisabled = () => {
      // Check if the slot is already booked
      const isBooked = currentBooking?.some((value) => {
        const startDateTime = new Date(value.startDateTime)
        const hours = startDateTime.getHours()

        if (startHour === hours) {
          return true
        } else {
          return false
        }
      })

      // Check if the slot is in the past
      const now = new Date()
      const selectedDateObj = new Date(selectedDate)
      const isToday = selectedDateObj.toDateString() === now.toDateString()

      if (isToday) {
        if (
          startHour < now.getHours() ||
          (startHour === now.getHours() && startMinutes <= now.getMinutes())
        ) {
          return true // Disable past time slots for today
        }
      }

      return isBooked
    }

    while (true) {
      if (startHour > endHour) {
        break
      }
      if (startHour === endHour) {
        if (startMinutes > endMinutes) {
          break
        }
      }

      let stringStartHour = `${startHour}`
      stringStartHour = stringStartHour.padStart(2, "0")
      let stringStartMinutes = `${startMinutes}`
      stringStartMinutes = stringStartMinutes.padStart(2, "0")

      const slot = stringStartHour + " : " + stringStartMinutes

      outputJsx.push(
        <button
          disabled={isbuttonSlotDisabled()}
          className={`grow text-xs font-bold w-max rounded-md border border-[--border-theme-300] py-2 px-3 ${slot === selectedSlot ? "bg-[--primary-blue] text-[--pure-white]" : "text-[--text-light-gray]"} ${isbuttonSlotDisabled() ? "border-gray-300 text-gray-300" : " "}`}
          data-time={slot}
          onClick={(event) => {
            const time = event.currentTarget.getAttribute("data-time")
            const [stringStartHour, stringStartMinutes] = time
              .split(":")
              .map((item) => {
                return parseInt(item)
              })
            // console.log(new Date(selectedDate))
            let startDateTime = new Date(selectedDate)
            let endDateTime = new Date(selectedDate)
            startDateTime.setHours(stringStartHour, stringStartMinutes, 0, 0)
            endDateTime.setHours(
              stringStartHour,
              appointmentDetails.duration + stringStartMinutes,
              0,
              0
            )
            setAppointmentDetails((previous) => {
              return { ...previous, startDateTime, endDateTime }
            })
            setSelectedSlot(time)
          }}
        >
          {convertTime(slot)}
        </button>
      )

      startMinutes += 60
      if (startMinutes === 60) {
        startMinutes = 0
        startHour++
      }
    }

    return outputJsx
  }

  const convertTime = (time) => {
    let hours = time.slice(0, 2)
    let minutes = time.slice(5, 7)

    if (hours === "12") {
      return time
    } else if (+hours > 12) {
      hours = +hours - 12
      hours = hours.toString().padStart(2, "0")
      minutes = minutes.toString().padStart(2, "0")
      return hours + " : " + minutes
    } else if (hours === "00") {
      minutes = minutes.toString().padStart(2, "0")
      return "12 : " + minutes
    } else {
      return time
    }
  }

  const handleDurationChange = (duration) => {
    if (appointmentDetails.startDateTime) {
      const startDateTime = new Date(appointmentDetails.startDateTime)
      const hours = startDateTime.getHours()
      const minutes = startDateTime.getMinutes()
      const endDateTime = new Date(appointmentDetails.startDateTime)
      endDateTime.setHours(hours, parseInt(duration) + minutes, 0, 0)

      setAppointmentDetails((previous) => {
        return { ...previous, duration, endDateTime }
      })
    } else {
      setAppointmentDetails((previous) => {
        return { ...previous, duration }
      })
    }
  }

  const handleLocationChange = (location) => {
    setAppointmentDetails((previous) => {
      return { ...previous, location }
    })
  }

  const handleTypeChange = (type) => {
    setAppointmentDetails((previous) => {
      return { ...previous, type }
    })
  }

  useEffect(() => {
    const currentDate = new Date()
    currentDate.setHours(0, 0, 0, 0)

    handleGetCalendarBooking(
      { date: currentDate.toISOString() },
      null,
      (error) => {
        navigate("/manage-appointment")
        notify(getErrorStatement(error))
      }
    )

    // Apply filter parameters if present
    if (filterParams) {
      if (filterParams.date) {
        const date = new Date(filterParams.date)
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        // Only apply date filter if it's not in the past
        if (date.getTime() >= today.getTime()) {
          handleGetCalendarBooking({ date: date.toISOString() })
          setSelectedDate(date.toISOString())
        } else {
          // If date is in the past, always force to today and show notification
          handleGetCalendarBooking({ date: today.toISOString() })
          setSelectedDate(today.toISOString())
          notify(
            "Cannot schedule appointments for past dates. Today's date has been selected."
          )
        }
      }
      if (filterParams.duration) {
        handleDurationChange(parseInt(filterParams.duration))
      }
      if (filterParams.type) {
        handleTypeChange(filterParams.type)
      }
      if (filterParams.location) {
        handleLocationChange(filterParams.location)
      }
    }
  }, [handleGetCalendarBooking, filterParams])

  return (
    <>
      {isGetCalendarBookingLoading ? <FullpageLoadingState /> : null}
      <div className="relative rounded-2xl sm:bg-[--pure-white] flex justify-center sm:justify-normal overflow-hidden">
        <div className="absolute bottom-0 right-0 z-[1] hidden xl:block">
          <MeetingInfoBackgroundIcon />
        </div>
        <div className="flex grow flex-wrap justify-center sm:justify-normal gap-4 sm:py-8 sm:px-10 h-full z-10 overflow-y-scroll no-scrollbar">
          <div className="flex flex-col gap-2 grow max-w-[22rem] max-h-[33.2rem]">
            <div className="sm:text-[#344054]">Select date:</div>
            <div className="text-sm text-[--primary-blue] font-medium mb-2">
              {selectedDate
                ? formatDateForDisplay(selectedDate)
                : "No date selected"}
            </div>
            <div className="border border-[--border-theme-300] rounded-2xl grow">
              <AppointmentCalendar
                defaultCollapse={false}
                rootClassNameOnExpand="h-full"
                onChange={handleCalendarDateChange}
                minDate={new Date()} // Setting minDate to today to prevent past date selection
                disablePastDates={true} // Add an explicit prop to disable past dates
              />
            </div>
          </div>
          <div className="flex flex-col gap-2 grow min-w-[21rem] max-w-[22rem] max-h-[33.2rem]">
            <div className="text-[--pure-white] sm:text-[#344054]">
              Select time
            </div>
            <div className="grow border border-[--border-theme-300] flex flex-col gap-4 rounded-2xl py-4 px-4 max-w-lg bg-[--pure-white] overflow-y-scroll no-scrollbar">
              <div className="flex flex-col gap-2">
                <div className="flex gap-2 items-center">
                  <SunFogIcon className="text-[#292D32]" />
                  <div className="tracking-widest text-xs font-bold">
                    MORNING (AM)
                  </div>
                </div>
                <div className="grid grid-cols-time-slot gap-2">
                  {appointmentTimingsJsx(0, 0, 11, 0)}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2 items-center">
                    <SunIcon
                      className="text-[#292D32] h-[1rem] w-[1rem]"
                      strokeWidth="5"
                    />

                    <div className="tracking-widest text-xs font-bold">
                      AFTERNOON (PM)
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-time-slot gap-2">
                  {appointmentTimingsJsx(12, 0, 23, 0)}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4 grow max-w-[22rem] sm:max-w-[24rem] p-4 bg-[--pure-white] sm:py-0 sm:bg-[--transparent] sm:rounded-none rounded-2xl h-min">
            <div className="flex flex-col gap-2">
              <div className="text-sm text-[#344054] font-medium">
                Select Duration*
              </div>
              <div className="flex">
                <div className="flex gap-1 py-2 px-3 border border-[#D0D5DD] grow rounded-l-lg bg-[--pure-white]">
                  <input
                    type="radio"
                    name="duration"
                    value="15"
                    onChange={() => {
                      handleDurationChange(15)
                    }}
                    checked={appointmentDetails.duration === 15}
                  ></input>
                  <label className="font-bold text-xs text-[#344054]">
                    15min
                  </label>
                </div>
                <div className="flex gap-1 py-2 px-3 border border-[#D0D5DD] grow bg-[--pure-white]">
                  <input
                    type="radio"
                    name="duration"
                    value="30"
                    onChange={() => {
                      handleDurationChange(30)
                    }}
                    checked={appointmentDetails.duration === 30}
                  ></input>
                  <label className="font-bold text-xs text-[#344054]">
                    30min
                  </label>
                </div>
                <div className="flex gap-1 py-2 px-3 border border-[#D0D5DD] grow bg-[--pure-white]">
                  <input
                    type="radio"
                    name="duration"
                    value="45"
                    onChange={() => {
                      handleDurationChange(45)
                    }}
                    checked={appointmentDetails.duration === 45}
                  ></input>
                  <label className="font-bold text-xs text-[#344054]">
                    45min
                  </label>
                </div>
                <div className="flex gap-1 py-2 px-3 border border-[#D0D5DD] grow rounded-r-lg bg-[--pure-white]">
                  <input
                    type="radio"
                    name="duration"
                    value="60"
                    defaultChecked
                    onChange={() => {
                      handleDurationChange(60)
                    }}
                    checked={appointmentDetails.duration === 60}
                  ></input>
                  <label className="font-bold text-xs text-[#344054]">
                    60min
                  </label>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <div className="text-sm text-[#344054] font-medium">
                Select Location*
              </div>
              <div className="flex gap-2">
                <div className="bg-[--primary-blue-light] border-[#D6EBFF] relative flex flex-col rounded-lg gap-1 py-2 px-3 border items-start basis-full">
                  <div className="absolute right-0 top-2 sm:top-auto sm:bottom-2">
                    <MonitorRecorderIcon />
                  </div>
                  <input
                    type="radio"
                    name="location"
                    value="online"
                    checked={appointmentDetails.location === "online"}
                    onChange={() => {
                      handleLocationChange("online")
                    }}
                  ></input>
                  <label className="font-bold text-xs text-[#344054] relative z-20 ">
                    <div>online</div>
                    <div className="text-[10px] text-[#344054] font-normal">
                      video consultation
                    </div>
                  </label>
                </div>
                <div className="relative bg-[--pure-white] flex flex-col rounded-lg gap-1 py-2 px-3 border border-[#D0D5DD] items-start basis-full">
                  <div className="absolute right-0 top-2 sm:top-auto sm:bottom-2">
                    <HospitalIcon className="text-[#D6EBFF]" />
                  </div>
                  <input
                    checked={appointmentDetails.location === "offline"}
                    type="radio"
                    name="location"
                    value="offline"
                    onChange={() => {
                      handleLocationChange("offline")
                    }}
                  ></input>
                  <label className="font-bold text-xs text-[#344054] relative z-20">
                    <div>offline</div>
                    <div className="text-[10px] font-normal text-[#344054]">
                      in person consultation
                    </div>
                  </label>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="text-sm text-[#344054] font-medium">
                Select Type*
              </div>
              <div className="flex">
                <div className="flex gap-1 py-2 px-3 border border-[#D0D5DD] grow rounded-l-lg bg-[--pure-white]">
                  <input
                    type="radio"
                    name="type"
                    value="followup"
                    onChange={() => {
                      handleTypeChange("followup")
                    }}
                  ></input>
                  <label className="font-bold text-xs text-[#344054]">
                    followup
                  </label>
                </div>
                <div className="flex gap-1 py-2 px-3 border border-[#D0D5DD] grow bg-[--pure-white]">
                  <input
                    type="radio"
                    name="type"
                    value="consultation"
                    defaultChecked
                    onChange={() => {
                      handleTypeChange("consultation")
                    }}
                  ></input>
                  <label className="font-bold text-xs text-[#344054]">
                    consultation
                  </label>
                </div>
                <div className="flex gap-1 py-2 px-3 border border-[#D0D5DD] grow rounded-r-lg bg-[--pure-white]">
                  <input
                    type="radio"
                    name="type"
                    value="prescription"
                    onChange={() => {
                      handleTypeChange("prescription")
                    }}
                  ></input>
                  <label className="font-bold text-xs text-[#344054]">
                    prescription
                  </label>
                </div>
              </div>
            </div>

            <Button
              disabled={isDisabled}
              onClick={() => {
                setAppointmentStage(2)
              }}
              className="!font-bold"
            >
              SAVE AND NEXT
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}

export default MeetingInfo
